import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { pt } from 'date-fns/locale';
import { BiCalendarAlt, BiDownload } from 'react-icons/bi';
import { getCampaignSummary, getCampaignStatusReport, getCampaignDispatchesExcel, getCampaignFirstDispatch } from '../../services/kollectaApi';
import Pagination from '../../Components/Pagination';
import HeaderInternal from '../../Components/HeaderInternal';
import CustomDateHeader from '../../Components/CustomDateHeader';
import { formatDateOnly } from '../../utils/formatDate';
import { formatNumber, formatPercent } from '../../utils/formatNumber';
import { parseCurrency } from '../../utils/currency';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './style.module.css';

const EMPTY_CARDS = {
  semInteracao: { total: 0, percent: 0 },
  enviadas: { total: 0, percent: 0 },
  lidas: { total: 0, percent: 0 },
  respondidas: { total: 0, percent: 0 },
  falha: { total: 0, percent: 0 },
};

const UNAVAILABLE_CARD = { total: null, percent: null, unavailable: true };
const UNAVAILABLE_CARDS = {
  semInteracao: UNAVAILABLE_CARD,
  enviadas: UNAVAILABLE_CARD,
  lidas: UNAVAILABLE_CARD,
  respondidas: UNAVAILABLE_CARD,
  falha: UNAVAILABLE_CARD,
};

const CARD_ITEMS = [
  { key: 'semInteracao', label: 'Sem interação', tone: 'cardYellow' },
  { key: 'enviadas', label: 'Mensagens enviadas', tone: 'cardPurple' },
  { key: 'lidas', label: 'Mensagens lidas', tone: 'cardGreen' },
  { key: 'respondidas', label: 'Mensagens respondidas', tone: 'cardBlue' },
  { key: 'falha', label: 'Falha no envio', tone: 'cardRed' },
];

const PRESETS = [
  { id: '7', label: '7 dias' },
  { id: '30', label: '30 dias' },
  { id: '90', label: '90 dias' },
  { id: 'all', label: 'Tudo' },
];

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const daysAgo = (days) => {
  const next = startOfDay(new Date());
  next.setDate(next.getDate() - days);
  return next;
};

const parseDayLocal = (isoDay) => {
  if (!isoDay) return startOfDay(new Date());
  const [year, month, day] = isoDay.split('-').map(Number);
  if (!year || !month || !day) return startOfDay(new Date());
  return startOfDay(new Date(year, month - 1, day));
};

const rangeFromPreset = (preset, firstDispatchDate) => {
  const end = startOfDay(new Date());
  if (preset === 'all') {
    return { start: parseDayLocal(firstDispatchDate), end };
  }
  return { start: daysAgo(Number(preset)), end };
};

const toUtcStartIso = (date) => (
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)).toISOString()
);

const toUtcEndIso = (date) => (
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)).toISOString()
);

const ruleDisplayName = (item) => {
  if (item.ruleName) return item.ruleName;
  if (item.ruleDeleted) return 'Regra removida';
  return '—';
};

const CampaignHistory = () => {
  const initialRange = rangeFromPreset('30');
  const [data, setData] = useState([]);
  const [cards, setCards] = useState(EMPTY_CARDS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardsError, setCardsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [preset, setPreset] = useState('30');
  const [rangeStart, setRangeStart] = useState(initialRange.start);
  const [rangeEnd, setRangeEnd] = useState(initialRange.end);
  const [draftStart, setDraftStart] = useState(initialRange.start);
  const [draftEnd, setDraftEnd] = useState(initialRange.end);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [firstDispatchDate, setFirstDispatchDate] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 8,
    totalItems: 0,
  });

  const fetchData = useCallback(async (page = 1) => {
    if (!rangeStart || !rangeEnd) return;

    setLoading(true);
    setError(null);
    try {
      const startDate = toUtcStartIso(rangeStart);
      const endDate = toUtcEndIso(rangeEnd);
      const pageSize = pagination.pageSize;

      const [summaryResult, reportResult] = await Promise.allSettled([
        getCampaignSummary(page, pageSize, startDate, endDate, true),
        getCampaignStatusReport({ startDate, endDate }),
      ]);

      if (summaryResult.status === 'fulfilled' && summaryResult.value.data?.data) {
        setData(summaryResult.value.data.data);
        setPagination(summaryResult.value.data.pagination || {
          page,
          pageSize,
          totalItems: 0,
        });
      } else {
        const summaryError = summaryResult.status === 'rejected' ? summaryResult.reason : null;
        setError(
          summaryError?.response?.data?.error
          || summaryError?.message
          || 'Erro ao buscar histórico de campanhas'
        );
        setData([]);
        setPagination({
          page,
          pageSize,
          totalItems: 0,
        });
      }

      if (reportResult.status === 'fulfilled' && reportResult.value.data?.cards) {
        setCards(reportResult.value.data.cards);
        setCardsError(null);
      } else {
        const reportError = reportResult.status === 'rejected' ? reportResult.reason : null;
        setCards(UNAVAILABLE_CARDS);
        setCardsError(
          reportError?.response?.data?.error
          || reportError?.message
          || 'Erro ao carregar resumo das campanhas'
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao buscar histórico de campanhas');
      setCardsError('Erro ao carregar resumo das campanhas');
      setData([]);
      setCards(UNAVAILABLE_CARDS);
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd, pagination.pageSize]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const userParse = JSON.parse(userData);
      setUser(userParse);
    }

    getCampaignFirstDispatch()
      .then(({ data }) => setFirstDispatchDate(data?.firstDate || null))
      .catch(() => setFirstDispatchDate(null));
  }, []);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const applyRange = (start, end, nextPreset) => {
    setRangeStart(start);
    setRangeEnd(end);
    setDraftStart(start);
    setDraftEnd(end);
    setPreset(nextPreset);
    setCurrentPage(1);
  };

  const handlePresetClick = async (presetId) => {
    let firstDate = firstDispatchDate;
    if (presetId === 'all' && !firstDate) {
      try {
        const { data } = await getCampaignFirstDispatch();
        firstDate = data?.firstDate || null;
        setFirstDispatchDate(firstDate);
      } catch (err) {
        firstDate = null;
      }
    }
    const nextRange = rangeFromPreset(presetId, firstDate);
    applyRange(nextRange.start, nextRange.end, presetId);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setDraftStart(start);
    setDraftEnd(end);
    if (start && end) {
      applyRange(start, end, null);
      setIsCalendarOpen(false);
    }
  };

  const toggleCalendar = () => {
    setDraftStart(rangeStart);
    setDraftEnd(rangeEnd);
    setIsCalendarOpen((open) => !open);
  };

  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.fullName) return user.fullName;
    if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName}`;
    return user?.email || '';
  };

  const dateRangeLabel = rangeStart && rangeEnd
    ? `${formatDateOnly(toUtcStartIso(rangeStart))} a ${formatDateOnly(toUtcEndIso(rangeEnd))}`
    : 'Selecione o período';

  const parseExportError = async (err) => {
    const data = err?.data ?? err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (parsed?.error) return parsed.error;
      } catch (parseError) {
        return 'Erro ao exportar relatório';
      }
    }
    if (typeof data?.error === 'string') return data.error;
    return err?.message || 'Erro ao exportar relatório';
  };

  const handleExport = async () => {
    if (!rangeStart || !rangeEnd || exporting) return;

    setExporting(true);
    setError(null);
    try {
      const startDate = toUtcStartIso(rangeStart);
      const endDate = toUtcEndIso(rangeEnd);
      const response = await getCampaignDispatchesExcel({ startDate, endDate });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `historico-campanhas-${startDate.slice(0, 10)}-${endDate.slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(downloadUrl);
      link.remove();
    } catch (err) {
      setError(await parseExportError(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={styles.container}>
      <HeaderInternal
        title={getUserDisplayName()}
        subtitle={user?.email || ''}
        profileImage={true}
        bold={true}
      />

      <div className={styles.content}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Histórico de campanhas</h1>
            <p className={styles.pageSubtitle}>
              {pagination.totalItems} campanhas entre {formatDateOnly(toUtcStartIso(rangeStart))} e {formatDateOnly(toUtcEndIso(rangeEnd))}
            </p>
          </div>

          <div className={styles.filters}>
            <div className={styles.presets}>
              {PRESETS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.presetButton} ${preset === item.id ? styles.presetButtonActive : ''}`}
                  onClick={() => handlePresetClick(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className={styles.datePickerWrap}>
              <button
                type="button"
                className={styles.dateButton}
                onClick={toggleCalendar}
              >
                {dateRangeLabel}
                <BiCalendarAlt />
              </button>
              {isCalendarOpen && (
                <div className={styles.datepickerPanel}>
                  <DatePicker
                    selected={draftStart}
                    onChange={handleDateChange}
                    startDate={draftStart}
                    endDate={draftEnd}
                    selectsRange
                    inline
                    locale={pt}
                    dateFormat="dd/MM/yyyy"
                    renderCustomHeader={(props) => <CustomDateHeader {...props} />}
                  />
                </div>
              )}
            </div>

            <button
              type="button"
              className={styles.exportButton}
              onClick={handleExport}
              disabled={exporting || !rangeStart || !rangeEnd}
            >
              <BiDownload />
              {exporting ? 'Exportando...' : 'Exportar relatório'}
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}
        {cardsError && (
          <div className={styles.error}>
            {cardsError}
          </div>
        )}

        <div className={styles.cards}>
          {CARD_ITEMS.map((item) => {
            const card = cards[item.key] || { total: 0, percent: 0 };
            const unavailable = Boolean(card.unavailable);
            const barWidth = unavailable
              ? 0
              : Math.min(Math.max(card.percent, 0), 100);
            return (
              <div key={item.key} className={`${styles.card} ${styles[item.tone]}`}>
                <span className={styles.cardLabel}>{item.label}</span>
                <strong className={styles.cardTotal}>
                  {unavailable ? '-' : formatNumber(card.total)}
                </strong>
                <span className={styles.cardPercent}>
                  {unavailable ? '-' : formatPercent(card.percent)}
                </span>
                <div className={styles.cardBarTrack} aria-hidden="true">
                  <div
                    className={styles.cardBarFill}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {loading ? (
          <div className={styles.loading}>Carregando...</div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Data de envio</th>
                    <th>Nome da regra</th>
                    <th>Canal</th>
                    <th className={styles.numberColumn}>Envios realizados</th>
                    <th className={styles.numberColumn}>Mensagens lidas</th>
                    <th className={styles.numberColumn}>Taxa de leitura</th>
                    <th className={styles.numberColumn}>Falha no envio</th>
                    <th className={styles.numberColumn}>Taxa de falha</th>
                    <th className={styles.numberColumn}>Respostas</th>
                    <th className={styles.numberColumn}>Taxa de Resposta</th>
                    <th className={styles.numberColumn}>Promessas de pagamento</th>
                    <th className={styles.numberColumn}>Acordos emitidos</th>
                    <th className={styles.numberColumn}>Acordos quitados</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan="13" className={styles.noData}>
                        {error ? 'Erro ao carregar dados' : 'Nenhum dado encontrado'}
                      </td>
                    </tr>
                  ) : (
                    data.map((item, index) => (
                      <tr key={item.batchId || item.dispatchKey || item.communicationRuleId || index}>
                        <td>{formatDateOnly(item.sendDate)}</td>
                        <td>{ruleDisplayName(item)}</td>
                        <td>
                          <span className={styles.channelBadge}>WhatsApp</span>
                        </td>
                        <td className={styles.numberColumn}>{formatNumber(item.enviosRealizados || 0)}</td>
                        <td className={styles.numberColumn}>{formatNumber(item.lidas || 0)}</td>
                        <td className={styles.numberColumn}>{formatPercent(item.taxaLeitura)}</td>
                        <td className={styles.numberColumn}>{formatNumber(item.falhaEnvio || 0)}</td>
                        <td className={styles.numberColumn}>{formatPercent(item.taxaFalha)}</td>
                        <td className={styles.numberColumn}>{formatNumber(item.respostas || 0)}</td>
                        <td className={styles.numberColumn}>{formatPercent(item.taxaResposta)}</td>
                        <td className={styles.numberColumn}>
                          {parseCurrency(item.promessaPagamento || 0)}
                        </td>
                        <td className={styles.numberColumn}>
                          {parseCurrency(item.acordosEmitidos || 0)}
                        </td>
                        <td className={styles.numberColumn}>
                          {parseCurrency(item.acordosQuitados || 0)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalItems > 0 && (
              <div className={styles.paginationWrapper}>
                <Pagination
                  currentPage={pagination.page}
                  setCurrentPage={handlePageChange}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.pageSize}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignHistory;
